import { createDefaultConfig } from './queryWorkspaceUtils'

// Defaults documented in the design's field-mapping table. They mirror exactly
// what `SemanticWorkspace.handleSelectSession` used to build inline, so restored
// history and live query responses share one shape (Req 5.1 - 5.5).
const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 25
const DEFAULT_TOTAL_PAGES = 1
const DEFAULT_MODE = 'rule'

/** Stable card id, with a fallback for environments without crypto.randomUUID. */
function createCardId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `card-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * First non-empty string among the candidates, or `''`.
 * Guarantees the text fields on the normalized response are always strings even
 * when a stored message carries a malformed value.
 * @param {...unknown} candidates
 * @returns {string}
 */
function firstText(...candidates) {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate !== '') return candidate
  }
  return ''
}

/**
 * Finite non-negative count, or the supplied fallback.
 * @param {unknown} value
 * @param {number} fallback
 * @returns {number}
 */
function toCount(value, fallback) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback
}

/** Plain object or the empty object — never an array or a primitive. */
function toRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

/** Pass-through for object-valued fields, normalizing anything else to null. */
function toObjectOrNull(value) {
  return value && typeof value === 'object' ? value : null
}

/**
 * True when a stored history message carries renderable assistant output.
 * Matches the original guard: an assistant message with SQL, a query, or data.
 * @param {object} message
 * @returns {boolean}
 */
export function isRenderableAssistantMessage(message) {
  if (!message || message.role !== 'assistant') return false
  return Boolean(message.generated_sql || message.query || message.data)
}

/**
 * Maps one stored assistant message onto the exact shape a live query response
 * has, so VisualizationSwitcher / createDefaultConfig / DataTableView consume
 * restored and live results through the same code path.
 *
 * @param {object} message stored assistant message
 * @param {string|null} sessionId session the message belongs to
 * @returns {{ mode: string, query: string, session_id: string|null,
 *             message_id: string|null, normalization: object|null,
 *             data: { columns: Array, rows: Array, row_count: number,
 *                     full_row_count: number, page: number, page_size: number,
 *                     total_pages: number, column_types: object },
 *             timing: object|null, visualization: object|null,
 *             metadata_used: object|null }}
 */
export function normalizeAssistantResponse(message, sessionId = null) {
  const msg = message && typeof message === 'object' ? message : {}
  const data = toRecord(msg.data)
  const rows = Array.isArray(data.rows) ? data.rows : []

  return {
    mode: firstText(msg.mode) || DEFAULT_MODE,
    // `query` wins over `generated_sql`; empty string when neither exists.
    query: firstText(msg.query, msg.generated_sql),
    session_id: sessionId ?? null,
    message_id: msg.message_id ?? null,
    normalization: toObjectOrNull(msg.normalization),
    // Chat-style message result: backend signals result_type="message" and puts
    // the reply text in `message` (no table/chart to render).
    result_type: firstText(msg.result_type) || null,
    message: firstText(msg.message) || null,
    data: {
      columns: Array.isArray(data.columns) ? data.columns : [],
      rows,
      row_count: toCount(data.row_count, rows.length),
      full_row_count: toCount(data.full_row_count, rows.length),
      page: toCount(data.page, DEFAULT_PAGE),
      page_size: toCount(data.page_size, DEFAULT_PAGE_SIZE),
      total_pages: toCount(data.total_pages, DEFAULT_TOTAL_PAGES),
      column_types: toRecord(data.column_types),
    },
    timing: toObjectOrNull(msg.timing),
    visualization: toObjectOrNull(msg.visualization),
    metadata_used: toObjectOrNull(msg.metadata_used),
  }
}

/**
 * Banner value for a card: normalization data is surfaced on the card only when
 * the backend actually rewrote the query (Req 5.5). The full normalization
 * object always stays on the response object regardless.
 * @param {object} message
 * @returns {object|null}
 */
function normalizationBannerValue(message) {
  const normalization = toObjectOrNull(message?.normalization)
  return normalization && normalization.was_modified === true ? normalization : null
}

/**
 * Pairs user/assistant messages and returns workspace cards, newest first —
 * the same ordering and card shape `SemanticWorkspace` already renders.
 * @param {Array<object>} messages session history in chronological order
 * @param {string|null} sessionId
 * @returns {Array<object>} cards
 */
export function buildCardsFromSessionMessages(messages, sessionId = null) {
  const history = Array.isArray(messages) ? messages : []
  const cards = []

  for (let i = 0; i < history.length; i++) {
    const msg = history[i]
    if (!isRenderableAssistantMessage(msg)) continue

    const userMsg = i > 0 && history[i - 1]?.role === 'user' ? history[i - 1] : null
    const queryText = firstText(userMsg?.content, msg.original_query)
    const response = normalizeAssistantResponse(msg, sessionId)
    const rows = response.data.rows

    cards.push({
      id: createCardId(),
      query: queryText,
      mode: response.mode,
      page: response.data.page,
      pageSize: response.data.page_size,
      isRestored: true,
      messageId: response.message_id,
      sessionId: sessionId ?? null,
      normalization: normalizationBannerValue(msg),
      response,
      data: rows,
      config: createDefaultConfig(rows, queryText, response),
      isMessage: response.result_type === 'message',
      message: response.message,
    })
  }

  return cards.reverse()
}
